import { useState } from "react";
import { NavLink, Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import NotificationBell from "./NotificationBell";

const NAV_ITEMS = [
  { to: "/account",  label: "Dashboard",         icon: "ri-dashboard-line",       end: true  },
  { to: "/listings", label: "My Listings",       icon: "ri-store-3-line",         end: false },
  { to: "/orders",   label: "My Orders",         icon: "ri-receipt-line",         end: false },
  { to: "/reviews",  label: "Reviews",           icon: "ri-chat-3-line",          end: false },
  { to: "/media",    label: "Media Library",     icon: "ri-image-2-line",         end: false },
  { to: "/profile",  label: "Business Profile",   icon: "ri-user-settings-line",  end: false },
];

interface PortalLayoutProps {
  children: React.ReactNode;
}

export default function PortalLayout({ children }: PortalLayoutProps) {
  const { user, logout, isLoading, isDemoMode } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  // The owner portal never exposes the admin console — it lives at /admin only.
  const navItems = NAV_ITEMS;

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background-50">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
          <p className="text-sm text-foreground-500">Loading your portal…</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  function handleLogout() {
    logout();
    navigate("/login");
  }

  const initials = user.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive
        ? "bg-primary-500 text-background-50"
        : "text-foreground-600 hover:bg-background-100 hover:text-foreground-950"
    }`;

  const Sidebar = () => (
    <aside className="flex h-full flex-col border-r border-background-200 bg-background-50">
      {/* Logo */}
      <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
        <Link to="/" className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-building-2-line text-base" />
          </span>
          <div>
            <p className="text-xs font-bold leading-tight text-foreground-950">Coastal Bizz</p>
            <p className="text-[10px] text-foreground-500">Owner Portal</p>
          </div>
        </Link>
        <NotificationBell />
      </div>

      {/* Demo banner */}
      {isDemoMode && (
        <div className="mx-3 mt-3 rounded-lg bg-accent-50 px-3 py-2.5 text-xs text-accent-800">
          <p className="flex items-center gap-1.5 font-semibold">
            <i className="ri-information-line" /> Demo Mode
          </p>
          <p className="mt-0.5 text-accent-700">Connect your backend to go live.</p>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4">
        {navItems.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} className={navClass} onClick={() => setSidebarOpen(false)}>
            <span className="flex h-5 w-5 items-center justify-center">
              <i className={`${item.icon} text-base`} />
            </span>
            {item.label}
          </NavLink>
        ))}

        <div className="my-3 border-t border-background-200" />

        <Link
          to="/"
          className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-600 transition-colors hover:bg-background-100 hover:text-foreground-950"
        >
          <span className="flex h-5 w-5 items-center justify-center">
            <i className="ri-arrow-left-line text-base" />
          </span>
          Back to Directory
        </Link>
      </nav>

      {/* User info */}
      <div className="border-t border-background-200 p-3">
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-foreground-900">{user.name}</p>
            <p className="truncate text-[10px] text-foreground-500">{user.email}</p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            title="Logout"
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-900"
          >
            <i className="ri-logout-box-r-line text-base" />
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background-100">
      {/* Desktop sidebar */}
      <div className="hidden w-56 shrink-0 lg:block">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setSidebarOpen(false)}>
          <div className="absolute inset-0 bg-foreground-950/40" />
          <div className="absolute left-0 top-0 h-full w-56 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <Sidebar />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile top bar */}
        <div className="flex items-center gap-3 border-b border-background-200 bg-background-50 px-4 py-3 lg:hidden">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700"
          >
            <i className="ri-menu-line text-base" />
          </button>
          <p className="text-sm font-semibold text-foreground-900">Owner Portal</p>
          <div className="ml-auto">
            <NotificationBell />
          </div>
        </div>

        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
}